import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../../app/Store";
import { resetSiteSpecificData } from "../../app/store/GlobalActions";
import {
  beginRequest,
  completeRequest,
  rejectRequest,
  RequestState,
} from "../../app/store/RequestState";
import { LoadingState } from "../../app/LoadingState";
import {
  IAddScheduledMaintenanceDto,
  IScheduledMaintenance,
  IScheduledMaintenanceDto,
  ScheduledMaintenanceType,
  IScheduledMaintenanceOverlapResult,
  IScheduledMaintenanceRecurringInput,
  IScheduledMaintenanceOneTimeInput,
} from "./Models";
import {
  getExistingScheduledMaintenance,
  deleteScheduledMaintenance,
  addScheduledMaintenance,
} from "./API";

interface ScheduleCommandState extends RequestState {
  oneTimeScheduleData: IScheduledMaintenanceOneTimeInput;
  recurringScheduleData: IScheduledMaintenanceRecurringInput;
  oneTimeError: string | null;
  recurringError: string | null;
  existingScheduledMaintenance: IScheduledMaintenance[];
  loading: boolean;
  deleteErrorMessage?: string;
  conflictsScheduledMaintenance?: IScheduledMaintenanceOverlapResult[];
  siteId: string;
}
const now = new Date().getTime();
const initialState: ScheduleCommandState = {
  loadingState: LoadingState.None,
  oneTimeScheduleData: {
    startDateTimeUtc: now,
    endDateTimeUtc: now,
  },
  recurringScheduleData: {
    repeatEveryWeek: 1,
    selectedDays: [],
    startTime: "",
    endTime: "",
    fromDateUtc: now,
    untilDateUtc: now,
  },
  oneTimeError: "",
  recurringError: "",
  existingScheduledMaintenance: [],
  loading: false,
  deleteErrorMessage: undefined,
  siteId: "",
};

const ScheduleCommandSlice = createSlice({
  name: "scheduleCommand",
  initialState,
  reducers: {
    setOneTimeScheduleData(state, action: PayloadAction<IScheduledMaintenanceOneTimeInput>) {
      state.oneTimeScheduleData = action.payload;
      state.oneTimeError = null;
    },
    setRecurringScheduleData(state, action: PayloadAction<IScheduledMaintenanceRecurringInput>) {
      state.recurringScheduleData = action.payload;
      state.recurringError = null;
    },
    setOneTimeError(state, action: PayloadAction<string>) {
      state.oneTimeError = action.payload;
    },
    setRecurringError(state, action: PayloadAction<string>) {
      state.recurringError = action.payload;
    },
    clearDeleteError(state) {
      state.deleteErrorMessage = undefined;
    },
    clearConflicts(state) {
      state.conflictsScheduledMaintenance = undefined;
    },
    setScheduleOneTimeError: (state, action: PayloadAction<string | null>) => {
      state.oneTimeError = action.payload;
    },
    setScheduleRecurringError: (state, action: PayloadAction<string | null>) => {
      state.recurringError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchScheduledMaintenance.fulfilled, (state, action) => {
      if (state.siteId !== action.meta.arg || !completeRequest(state, action.meta.requestId)) {
        return;
      }
      const data = action.payload?.map((item) => mapScheduledMaintenanceDtoToScheduledMaintenance(item));
      state.existingScheduledMaintenance = data;
      state.loading = false;
    });
    builder.addCase(fetchScheduledMaintenance.pending, (state, action) => {
      const newSiteId = action.meta.arg;
      const nextState = state.siteId === newSiteId ? state : { ...initialState, siteId: newSiteId };
      nextState.loading = true;
      beginRequest(nextState, action.meta.requestId);
      return nextState;
    });
    builder.addCase(fetchScheduledMaintenance.rejected, (state, action) => {
      if (rejectRequest(state, action)) {
        state.loading = state.loadingState === LoadingState.Pending;
      }
    });

    builder.addCase(deleteScheduleMaintenance.rejected, (state, action) => {
      if (state.siteId === action.meta.arg.siteId && !action.meta.aborted) {
        state.deleteErrorMessage = "Failed to delete scheduled maintenance. Please try again later.";
      }
    });

    builder.addCase(addScheduleMaintenance.pending, (state) => {
      state.conflictsScheduledMaintenance = undefined;
    });

    builder.addCase(addScheduleMaintenance.rejected, (state, action) => {
      if (state.siteId === action.meta.arg.scheduledData.siteId && !action.meta.aborted) {
        handleAddScheduleMaintenanceRejected(state, action.payload);
      }
    });
    builder.addCase(resetSiteSpecificData, () => initialState);
  },
});

const handleAddScheduleMaintenanceRejected = (
  state: ScheduleCommandState,
  payload?: {
    conflicts?: IScheduledMaintenanceOverlapResult[];
    message?: string;
  }
) => {
  if (payload?.conflicts) {
    state.conflictsScheduledMaintenance = payload.conflicts;
  }
};

export const fetchScheduledMaintenance = createAsyncThunk(
  "scheduleCommand/fetchScheduledMaintenance",
  async (siteId: string, { signal }) => {
    return await getExistingScheduledMaintenance(siteId, signal);
  }
);

export const deleteScheduleMaintenance = createAsyncThunk(
  "scheduleCommand/deleteScheduleMaintenance",
  async ({ siteId, scheduledId }: { siteId: string; scheduledId: string }, { dispatch, getState }) => {
    await deleteScheduledMaintenance(siteId, scheduledId);
    if ((getState() as RootState).site.siteId === siteId) {
      dispatch(fetchScheduledMaintenance(siteId));
    }
    return scheduledId;
  }
);

export const addScheduleMaintenance = createAsyncThunk<
  IScheduledMaintenanceDto,
  { ianaTimeZone: string; scheduledData: IAddScheduledMaintenanceDto },
  {
    rejectValue: {
      conflicts?: IScheduledMaintenanceOverlapResult[];
      message?: string;
    };
  }
>(
  "scheduleCommand/addScheduleMaintenance",
  async ({ ianaTimeZone, scheduledData }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await addScheduledMaintenance(ianaTimeZone, scheduledData);
      if ((getState() as RootState).site.siteId === scheduledData.siteId) {
        dispatch(fetchScheduledMaintenance(scheduledData.siteId));
      }
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          return rejectWithValue({
            conflicts: error.response.data,
          });
        }
        return rejectWithValue({
          message:
            error.response?.data?.message || "Failed to add scheduled maintenance. Please try again",
        });
      }
      return rejectWithValue({
        message: "An unexpected error occurred. Please try again.",
      });
    }
  }
);

const mapScheduledMaintenanceDtoToScheduledMaintenance = (
  item: IScheduledMaintenanceDto
): IScheduledMaintenance => {
  return {
    ...item,
    scheduleType: item.recurrence
      ? ScheduledMaintenanceType.Recurring
      : ScheduledMaintenanceType.OneTime,
  };
};

export const {
  setOneTimeScheduleData,
  setRecurringScheduleData,
  setOneTimeError,
  setRecurringError,
  clearDeleteError,
  clearConflicts,
  setScheduleOneTimeError,
  setScheduleRecurringError,
} = ScheduleCommandSlice.actions;

export const scheduleCommandReducer = ScheduleCommandSlice.reducer;
